import React from 'react';
import ManoirBlason from '@/components/ui/ManoirBlason';
import { t, isLangLoaded } from '@/lib/i18n';
import { MEDARD } from '@/constants/testIds';

/**
 * AppErrorBoundary — the app's last line of defence. Individual views already
 * guard their risky chunks (Balade map, Explorer Leaflet), but a runtime error
 * anywhere else would unmount React and leave the guest on a blank white page
 * — the opposite of « servir avec chaleur » (Loi 3). This top-level boundary
 * catches any such error and shows a calm, on-brand fallback with a reload,
 * so a Manoir guest on flaky wifi is never dropped into a void.
 *
 * A class component is required — only class components can be error
 * boundaries (getDerivedStateFromError / componentDidCatch). It holds no other
 * state and does no i18n gymnastics: it reads a best-effort language the same
 * way the pre-app RouteFallback does (stored choice → browser → French), since
 * the app's own lang state may be exactly what failed to mount.
 */

function bestEffortLang() {
    try {
        const stored = window.localStorage.getItem('medard_lang');
        // Only a language whose dictionary is LOADED (audit 10/09 bis, perf
        // n°4: one chunk per language) — `t()` would answer in French for
        // any other, so the choice is made here, once, rather than half-way.
        if (stored && isLangLoaded(stored)) return stored;
    } catch { /* private mode */ }
    if (typeof navigator !== 'undefined') {
        const code = (navigator.language || '').slice(0, 2).toLowerCase();
        if (isLangLoaded(code)) return code;
    }
    return 'fr';
}

export default class AppErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
        this.handleReload = this.handleReload.bind(this);
    }

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error, info) {
        // Surface it for diagnostics without ever re-throwing to the guest.
        // eslint-disable-next-line no-console
        console.error('[medard] app crash caught by boundary:', error, info);
    }

    handleReload() {
        try {
            window.location.reload();
        } catch {
            /* nothing else we can do */
        }
    }

    render() {
        if (!this.state.failed) return this.props.children;

        const lang = bestEffortLang();
        return (
            <div
                data-testid={MEDARD.appError}
                role="alert"
                className="fixed inset-0 z-[100] flex flex-col items-center justify-center text-center bg-aman-ivory px-8"
                style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
                <ManoirBlason className="w-14 h-16 md:w-16 md:h-[4.75rem] select-none mb-7" />
                <h1 className="font-display text-3xl md:text-4xl text-manoir-delft tracking-tight leading-tight mb-4">
                    {t(lang, 'appErrorTitle')}
                </h1>
                <p className="font-display italic text-manoir-ink-soft text-base md:text-lg max-w-md mb-8">
                    {t(lang, 'appErrorBody')}
                </p>
                <button
                    type="button"
                    data-testid={MEDARD.appErrorReload}
                    onClick={this.handleReload}
                    className="group min-h-[56px] px-10 py-4 border border-manoir-gold bg-manoir-gold/10 text-aman-ink shadow-aman-relief hover:bg-manoir-gold/25 hover:shadow-aman-relief-lift active:bg-manoir-gold/25 transition-[background-color,box-shadow] duration-300 text-sm uppercase tracking-display font-medium"
                >
                    <span>{t(lang, 'appErrorReload')}</span>
                    <span className="ml-3 inline-block motion-safe:transition-transform motion-safe:group-hover:translate-x-1">→</span>
                </button>
            </div>
        );
    }
}
